interface Action {
  label: string;
  message?: string;
}

interface RecommendedActionsProps {
  content: string;
  metadata?: unknown;
  onAction?: (message: string) => void;
  disabled?: boolean;
}

function getTitle(metadata: unknown): string | null {
  if (
    metadata &&
    typeof metadata === 'object' &&
    'title' in metadata &&
    typeof (metadata as Record<string, unknown>).title === 'string'
  ) {
    return (metadata as Record<string, unknown>).title as string;
  }
  return null;
}

function parseActions(content: string): Action[] {
  try {
    const parsed: unknown = JSON.parse(content);
    if (Array.isArray(parsed)) {
      return parsed.map((item: unknown) => {
        if (typeof item === 'string') return { label: item };
        if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, unknown>;
          return {
            label: (obj.label as string) ?? String((obj.message as string | number) ?? ''),
            message: obj.message as string | undefined,
          };
        }
        return { label: String(item) };
      });
    }
  } catch {
    // If not valid JSON, treat each line as an action
    return content
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((label) => ({ label }));
  }
  return [];
}

export function RecommendedActions({
  content,
  metadata,
  onAction,
  disabled,
}: RecommendedActionsProps): React.ReactElement {
  const actions = parseActions(content);
  const title = getTitle(metadata);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="recommended-actions">
      {title && (
        <div className="px-4 py-3 text-xs font-semibold tracking-wider text-muted-foreground/60 uppercase">{title}</div>
      )}
      {actions.map((action, i) => (
        <button
          key={i}
          disabled={disabled}
          onClick={() => onAction?.(action.message ?? action.label)}
          className="flex w-full items-center border-t border-border/50 px-4 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-background hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}
